/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import { Accordion } from "@/components/core/accordion";
import { Shell } from "@/components/ui/shell";
import FAQItem from "./FAQItem";
import { SubjectSelector } from "./SubjectSelector";
import { useEffect, useState } from "react";
import { getFAQs } from "@/services/faqs/faq.client";
import { useLocaleCtx } from "@/contexts/locale";
import { useTranslation } from "@/i18n/client";
import FAQs from "@/i18n/locales/en/faq.json";
import Carpet from "@/components/ui/Carpet";

export default function FAQContent() {
  const [faqs, setFaqs] = useState<unknown[]>([]);
  const [subject, setSubject] = useState("");

  const { lang } = useLocaleCtx();

  const fetchFAQs = async (subject: string) => {
    try {
      const res = await getFAQs(lang, subject);
      setFaqs(res?.data);
    } catch (e: unknown) {
      console.error(e);
    }
  };
  const { t } = useTranslation(lang, "faq");

  const subjects = t("subjects", {
    returnObjects: true,
  }) as (typeof FAQs)["subjects"];

  useEffect(() => {
    fetchFAQs(subject.trim());
  }, [subject]);

  const faqsBySubject: Record<string, typeof faqs> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  faqs?.forEach((faq: any) => {
    if (!faqsBySubject[faq?.faq_subject?.title]) {
      faqsBySubject[faq?.faq_subject?.title] = [];
    }
    faqsBySubject[faq?.faq_subject?.title].push(faq);
  });

  return (
    <div className="min-h-dvh">
      <Carpet type="fade-light-to-dark" className="pb-10 pt-16 lg:pt-24 xl:pt-40">
        <Shell>
          <div className="flex flex-col justify-between gap-8 pb-12 sm:flex-row sm:gap-0">
            <h1 className="text-2xl text-secondary md:text-3xl ltr:font-playfair">
              {lang === "en" ? "Frequently Asked Questions" : "الأسئلة المتداولة"}
            </h1>
            <div className="flex items-center gap-2 font-opensans text-sm text-secondary/40">
              <p className="text-gold-medium max-[300px]:hidden min-[500px]:flex-shrink-0 xl:text-lg">
                {lang === "en" ? "Filter by:" : "تصفية حسب"}
              </p>
              <SubjectSelector setSubject={setSubject} subjects={subjects} />
            </div>
          </div>

          <div className="flex flex-col gap-8 lg:flex-row lg:justify-between">
            <div className="flex w-full flex-col gap-14 lg:w-[48%]">
              {Object.keys(faqsBySubject)
                .slice(0, 3)
                .map((subject) => (
                  <div key={subject} className="flex flex-col gap-4">
                    <h1 className="mb-8 text-lg text-secondary sm:text-2xl ltr:font-playfair">
                      {subject}
                    </h1>
                    <Accordion
                      className="flex w-full flex-col gap-4"
                      transition={{
                        type: "spring",
                        stiffness: 120,
                        damping: 20,
                      }}
                      variants={{
                        expanded: {
                          opacity: 1,
                          scale: 1,
                        },
                        collapsed: {
                          opacity: 0,
                          scale: 0.7,
                        },
                      }}
                    >
                      {faqsBySubject[subject].map((faq: any) => (
                        <FAQItem key={faq.id} faq={faq} />
                      ))}
                    </Accordion>
                  </div>
                ))}
            </div>

            <div className="flex w-full flex-col gap-14 lg:w-[48%]">
              {Object.keys(faqsBySubject)
                .slice(3, 6)
                .map((subject) => (
                  <div key={subject} className="flex flex-col gap-4">
                    <h1 className="mb-8 text-lg text-secondary sm:text-2xl ltr:font-playfair">
                      {subject}
                    </h1>
                    <Accordion
                      className="flex w-full flex-col gap-4"
                      transition={{
                        type: "spring",
                        stiffness: 120,
                        damping: 20,
                      }}
                      variants={{
                        expanded: {
                          opacity: 1,
                          scale: 1,
                        },
                        collapsed: {
                          opacity: 0,
                          scale: 0.7,
                        },
                      }}
                    >
                      {faqsBySubject[subject].map((faq: any) => (
                        <FAQItem key={faq.id} faq={faq} />
                      ))}
                    </Accordion>
                  </div>
                ))}
            </div>
          </div>
        </Shell>
      </Carpet>
    </div>
  );
}
